import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaShelf from "@/components/feature/DramaShelf";
import PosterCarousel from "@/components/feature/PosterCarousel";
import DramaCard from "@/components/feature/DramaCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { dramas } from "@/mocks/dramas";
import { categories, quickFilters } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";
import { sortDramas } from "@/utils/drama";

const quickCopy: Record<string, { title: string; subtitle: string }> = {
  update: { title: "最新更新", subtitle: "刚刚上线的新剧，抢先一集看完" },
  weekly: { title: "本周热播", subtitle: "近七天播放增长最快的爆款短剧" },
  finished: { title: "完结精选", subtitle: "全集已完结，不用等更新一次追到底" },
  top: { title: "高分推荐", subtitle: "评分 8.5 以上，口碑与热度双高" },
};

export default function Home() {
  const [quick, setQuick] = useState("update");

  const weekly = useMemo(() => sortDramas(dramas, "hot").slice(0, 6), []);
  const topRated = useMemo(
    () => sortDramas(dramas.filter((item) => item.status === "已完结"), "rating").slice(0, 6),
    []
  );
  const exclusive = useMemo(() => dramas.filter((item) => item.exclusive).slice(0, 8), []);
  const fresh = useMemo(() => sortDramas(dramas, "new").slice(0, 6), []);

  const quickList = useMemo(() => {
    if (quick === "weekly") return sortDramas(dramas, "hot").slice(0, 6);
    if (quick === "finished") return sortDramas(dramas.filter((item) => item.status === "已完结"), "rating").slice(0, 6);
    if (quick === "top") return sortDramas(dramas, "rating").slice(0, 6);
    return sortDramas(dramas, "new").slice(0, 6);
  }, [quick]);

  useSeo({
    title: "剧焰短剧 | 热门微短剧高清全集在线观看 - 霸道总裁·都市战神·甜宠虐恋",
    description:
      "剧焰短剧聚合全网热门微短剧，提供霸道总裁、都市战神、战神归来、甜宠虐恋、穿越复仇、古装权谋等题材高清全集在线观看，每日更新最新短剧榜单与高分完结精选。",
    keywords:
      "微短剧,短剧在线观看,霸道总裁短剧,都市战神短剧,甜宠虐恋,穿越复仇短剧,古装权谋,短剧排行榜,免费短剧网站",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "剧焰短剧 · 热门微短剧在线观看",
      description: "聚合全网热门微短剧的高清在线观看平台，每日更新最新剧集与榜单。",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        name: "本周热播微短剧",
        itemListElement: weekly.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `https://www.juyan-drama.com/drama/${item.id}`,
        })),
      },
    },
  });

  const copy = quickCopy[quick];

  return (
    <>
      <SiteHeader />
      <main className="w-full">
        <HeroCarousel />

        <div className="w-full px-4 pb-4 md:px-6">
          {/* 快捷筛选 Tag 栏 */}
          <section aria-labelledby="quick-heading" className="mt-8 md:mt-12">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="剧集快捷筛选">
                {quickFilters.map((item) => {
                  const active = item.key === quick;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setQuick(item.key)}
                      className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-medium transition-colors md:text-sm ${
                        active
                          ? "bg-primary-500 text-background-950 shadow-lg shadow-primary-950/40"
                          : "border border-foreground-50/10 bg-foreground-50/[0.04] text-foreground-300 hover:border-primary-400/50 hover:text-primary-300"
                      }`}
                    >
                      <i className={`${item.icon} text-sm`} aria-hidden="true" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-400 transition-colors hover:text-primary-300 md:text-sm"
              >
                全部剧集
                <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
              </Link>
            </div>

            <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 md:mt-6">
              <h2 id="quick-heading" className="font-heading text-lg font-bold text-foreground-50 md:text-xl">
                {copy.title}
              </h2>
              <p className="text-xs text-foreground-500 md:text-sm">{copy.subtitle}</p>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 xl:grid-cols-6">
              {quickList.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </section>

          {/* 分类速览 */}
          <section aria-labelledby="channel-heading" className="mt-10 md:mt-14">
            <h2 id="channel-heading" className="font-heading text-lg font-bold text-foreground-50 md:text-xl">
              按题材找剧
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4 md:gap-3">
              {categories.map((item) => (
                <Link
                  key={item.key}
                  to={`/category?type=${item.key}`}
                  className="group flex cursor-pointer items-center justify-between rounded-xl border border-foreground-50/10 bg-gradient-to-br from-foreground-50/[0.06] to-transparent px-3.5 py-3 transition-colors hover:border-primary-400/50"
                >
                  <span className="text-sm text-foreground-200 transition-colors group-hover:text-primary-300">
                    {item.label}
                  </span>
                  <span className="flex h-5 w-5 items-center justify-center text-foreground-600 transition-colors group-hover:text-primary-400">
                    <i className="ri-arrow-right-up-line text-base" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <DramaShelf
            headingId="weekly-heading"
            title="本周热播榜"
            subtitle="实时热度排序，追剧不踩雷"
            dramas={weekly}
            moreHref="/category?sort=hot"
            ranked
          />

          <DramaShelf
            headingId="finished-heading"
            title="高分完结精选"
            subtitle="一次看完，拒绝等更新"
            dramas={topRated}
            moreHref="/category?status=done&sort=rating"
          />

          <PosterCarousel
            headingId="exclusive-heading"
            title="独家首发"
            subtitle="剧焰短剧独家版权，抢先观看"
            dramas={exclusive}
            moreHref="/category?type=ceo"
          />

          <DramaShelf
            headingId="fresh-heading"
            title="新剧上线"
            subtitle="最新上线的短剧，第一时间追"
            dramas={fresh}
            moreHref="/category"
          />
        </div>
      </main>
      <SiteFooter />
    </>
  );
}